import { useFormContext } from 'react-hook-form'

import {
  convertLocalToUTCDate,
  convertUTCToLocalDate,
  isValidDate,
} from '@/lib/helpers'

import { requiredError } from './constants'
import {
  getFirstAndLastDayOfMonth,
  getFirstAndLastDayOfYear,
  getNestedValue,
} from './helpers'

import type {
  OnRangeDateChange,
  OnSingleDateChange,
  UseDatePickerProps,
} from './types'
import type { FieldError } from 'react-hook-form'

export const useDatePicker: UseDatePickerProps = ({
  dateStartKey,
  dateEndKey,
  name,
  onChange,
  isRequired,
  selected,
  mode,
  selectsRange,
}) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext()

  const startKey = dateStartKey || `${name}.checkIn`
  const endKey = dateEndKey || `${name}.checkOut`
  const noRangeKey = name

  const startDate = watch(startKey)
  const endDate = watch(endKey)

  const handleDateChangeInForm = (date: Date, key: string) => {
    clearErrors(key)
    if (!date && isRequired) {
      setError(key, requiredError)
    }

    setValue(key, date)
  }

  const getStartAndEndDatesInUTC = (dates: [Date, Date]) => {
    const [startDate] = dates
    if (mode === 'year') {
      return getFirstAndLastDayOfYear(startDate)
    }
    if (mode === 'month') {
      return getFirstAndLastDayOfMonth(startDate)
    }

    return [convertLocalToUTCDate(dates[0]), convertLocalToUTCDate(dates[1])]
  }

  const handleRangeDatesChange: OnRangeDateChange = (dates) => {
    const [utcStart, utcEnd] = getStartAndEndDatesInUTC(dates)

    handleDateChangeInForm(utcStart, startKey)
    handleDateChangeInForm(utcEnd, endKey)

    if (onChange) {
      onChange([utcStart, utcEnd])
    }
  }

  const handleSingleDateChange: OnSingleDateChange = (date) => {
    const utcFormat = convertLocalToUTCDate(date)

    handleDateChangeInForm(utcFormat, noRangeKey)

    if (onChange) {
      onChange(utcFormat)
    }
  }

  const error =
    getNestedValue(errors, startKey) ||
    (getNestedValue(errors, endKey) as FieldError)

  return {
    control,
    startDate:
      startDate && isValidDate(startDate)
        ? convertUTCToLocalDate(startDate)
        : null,
    endDate:
      endDate && isValidDate(endDate) ? convertUTCToLocalDate(endDate) : null,
    selectedDate:
      selected && isValidDate(selected)
        ? convertUTCToLocalDate(selected)
        : null,
    handleDatesChange: selectsRange
      ? handleRangeDatesChange
      : handleSingleDateChange,
    error,
  }
}
